import { createContext, useReducer, useCallback } from "react";

import useHome from "../../hooks/user/useHome";
import axios from "../../utils/axios/v1/userAxios";

const GET_ALL_FIREWALL = "GET_ALL_FIREWALL";
const GET_SINGLE_FIREWALL = "GET_SINGLE_FIREWALL";

const GET_TOP_SOURCE = "GET_TOP_SOURCE";
const GET_TOP_PATH = "GET_TOP_PATH";
const GET_TOP_UA = "GET_TOP_UA";
const GET_TOP_METHOD = "GET_TOP_METHOD";
const GET_TOP_JA3_HASH = "GET_TOP_JA3_HASH";
const GET_TOP_RESPONSE_CODE = "GET_TOP_RESPONSE_CODE";
const CLEAR_FIREWALLS = "CLEAR_FIREWALLS";

const SET_ERROR = "SET_ERROR";

const initialState = {
  firewalls: null,
  curfirewall: null,

  top_source: null,
  top_path: null,
  top_ua: null,
  top_method: null,
  top_ja3_hash: null,
  top_response_code: null,

  errMsg: null,
};

const FirewallReducer = (state, action) => {
  switch (action.type) {
    case GET_ALL_FIREWALL:
      return {
        ...state,
        firewalls: action.payload.firewalls,
      };
    case GET_SINGLE_FIREWALL:
      return {
        ...state,
        curfirewall: action.payload.curfirewall,
      };
    case GET_TOP_SOURCE:
      return {
        ...state,
        top_source: action.payload.top_source,
      };
    case GET_TOP_PATH:
      return {
        ...state,
        top_path: action.payload.top_path,
      };
    case GET_TOP_UA:
      return {
        ...state,
        top_ua: action.payload.top_ua,
      };
    case GET_TOP_METHOD:
      return {
        ...state,
        top_method: action.payload.top_method,
      };
    case GET_TOP_JA3_HASH:
      return {
        ...state,
        top_ja3_hash: action.payload.top_ja3_hash,
      };
    case GET_TOP_RESPONSE_CODE:
      return {
        ...state,
        top_response_code: action.payload.top_response_code,
      };
    case CLEAR_FIREWALLS:
      return {
        ...state,
        firewalls: null,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const FirewallContext = createContext(null);

function FirewallProvider({ children }) {
  const { curProjectId } = useHome();
  const [state, dispatch] = useReducer(FirewallReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);
  const clearFirewalls = useCallback(() => {
    dispatch({ type: CLEAR_FIREWALLS });
  }, []);
  const getFirewalls = useCallback(
    async (siteUid, init = false) => {
      if (init)
        dispatch({
          type: GET_ALL_FIREWALL,
          payload: {
            firewalls: null,
          },
        });
      try {
        const response = await axios.get(`/config/fw/${siteUid}`);
        dispatch({
          type: GET_ALL_FIREWALL,
          payload: {
            firewalls: response.data,
          },
        });
      } catch (err) {
        dispatch({
          type: GET_ALL_FIREWALL,
          payload: {
            firewalls: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );
  const updateFirewall = useCallback(
    async (siteUid, firewallID, value) => {
      try {
        await axios.patch(`/config/fw/${siteUid}/${firewallID}`, value);
        getFirewalls(siteUid);
        return { msg: "Success", status: "success" };
      } catch (err) {
        setErr(err.message);
        getFirewalls(siteUid);
        let error_msg = err.message;
        return { msg: error_msg, status: "error" };
      }
    },
    [setErr, getFirewalls]
  );
  const deleteFirewall = useCallback(
    async (siteUid, value) => {
      try {
        await axios.delete(`config/fw/${siteUid}`, { data: value });
        getFirewalls(siteUid, true);
      } catch (err) {
        setErr(err.message);
      }
    },
    [getFirewalls, setErr]
  );
  const addNewFirewall = useCallback(
    async (siteUid, value) => {
      try {
        await axios.post(`/config/fw/${siteUid}`, value);
        getFirewalls(siteUid);
        return { msg: "Success", status: "success" };
      } catch (err) {
        setErr(err.message);
        let error_msg = err.message;
        return { msg: error_msg, status: "error" };
      }
    },
    [getFirewalls, setErr]
  );
  const getCurrentFirewall = useCallback(
    async (siteUid, firewallID) => {
      try {
        const response = await axios.get(`/config/fw/${siteUid}/${firewallID}`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return [];
      }
    },
    [setErr]
  );
  const saveFwRulesOrder = useCallback(
    async (siteUid, value) => {
      try {
        await axios.patch(`/config/fw/${siteUid}/set_order`, value);
        return { msg: "Success", status: "success" };
      } catch (err) {
        setErr(err.message);
        let error_msg = err.message;
        return { msg: error_msg, status: "error" };
      }
    },
    [setErr]
  );
  const getTopSource = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_SOURCE,
        payload: {
          top_source: null,
        },
      });
      try {
        const response = await axios.post(
          "fw_stats/top_source",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_SOURCE,
          payload: {
            top_source: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_SOURCE,
          payload: {
            top_source: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopPath = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_PATH,
        payload: {
          top_path: null,
        },
      });
      try {
        const response = await axios.post(
          "fw_stats/top_path",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_PATH,
          payload: {
            top_path: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_PATH,
          payload: {
            top_path: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopUA = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_UA,
        payload: {
          top_ua: null,
        },
      });
      try {
        const response = await axios.post(
          "fw_stats/top_ua",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_UA,
          payload: {
            top_ua: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_UA,
          payload: {
            top_ua: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopMethod = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_METHOD,
        payload: {
          top_method: null,
        },
      });
      try {
        const response = await axios.post(
          "fw_stats/top_method",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_METHOD,
          payload: {
            top_method: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_METHOD,
          payload: {
            top_method: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopJa3Hash = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_JA3_HASH,
        payload: {
          top_ja3_hash: null,
        },
      });
      try {
        const response = await axios.post(
          "fw_stats/top_ja3_hash",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_JA3_HASH,
          payload: {
            top_ja3_hash: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_JA3_HASH,
          payload: {
            top_ja3_hash: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopResponseCode = useCallback(
    async (site_id, time_range, size, signal) => {
      dispatch({
        type: GET_TOP_RESPONSE_CODE,
        payload: {
          top_response_code: null,
        },
      });
      try {
        const response = await axios.post(
          "fw_stats/top_res_code",
          {
            site_id: site_id,
            project_id: curProjectId,
            time_range: time_range,
            size: size,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_RESPONSE_CODE,
          payload: {
            top_response_code: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_RESPONSE_CODE,
          payload: {
            top_response_code: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, curProjectId]
  );
  const getTopFwEventsInfo = useCallback(
    (setWafEventController, site_id, time_range) => {
      const controller = new AbortController();
      const signal = controller.signal;
      setWafEventController(controller);

      getTopSource(site_id, time_range, undefined, signal);
      getTopPath(site_id, time_range, undefined, signal);
      getTopUA(site_id, time_range, undefined, signal);
      getTopMethod(site_id, time_range, undefined, signal);
      getTopResponseCode(site_id, time_range, undefined, signal);
      getTopJa3Hash(site_id, time_range, undefined, signal);
    },
    [getTopSource, getTopPath, getTopUA, getTopMethod, getTopResponseCode, getTopJa3Hash]
  );
  return (
    <FirewallContext.Provider
      value={{
        ...state,
        setErr,
        clearFirewalls,
        getFirewalls,
        updateFirewall,
        deleteFirewall,
        addNewFirewall,
        getCurrentFirewall,
        saveFwRulesOrder,
        getTopSource,
        getTopPath,
        getTopUA,
        getTopMethod,
        getTopJa3Hash,
        getTopFwEventsInfo,
      }}
    >
      {children}
    </FirewallContext.Provider>
  );
}

export { FirewallContext, FirewallProvider };
